/**
 * Auth Composable
 *
 * Provides authentication functionality with Vue reactivity.
 * Wraps the auth store for convenient use in components.
 */
import { computed } from 'vue'
import axios from 'axios'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import { useNotifications } from './useNotifications'
import { getApiErrorMessage } from '../services/api'
import type { LoginRequest } from '../types/auth'

/** A real credential rejection (400/401), as opposed to the server refusing or failing. */
function isCredentialRejection(error: unknown): boolean {
  const status = axios.isAxiosError(error) ? error.response?.status : undefined
  return status === 400 || status === 401
}

/**
 * What a failed sign-in says. Only a credential rejection reads "Invalid email
 * or password"; a rate limit, an unavailable sign-in, a network drop or a 5xx
 * during a deploy each keep their own wording via getApiErrorMessage.
 */
export function loginFailureMessage(error: unknown): string {
  return isCredentialRejection(error) ? 'Invalid email or password' : getApiErrorMessage(error)
}

export function useAuth() {
  const authStore = useAuthStore()
  const router = useRouter()
  const route = useRoute()
  const { showSuccess, showError } = useNotifications()

  // Reactive getters
  const user = computed(() => authStore.user)
  const isAuthenticated = computed(() => authStore.isAuthenticated)
  const isLoading = computed(() => authStore.loading)
  const isAdmin = computed(() => authStore.isAdmin)
  const isStaff = computed(() => authStore.isStaff)
  const displayName = computed(() => authStore.userDisplayName)
  const initials = computed(() => authStore.userInitials)

  /**
   * Login and redirect to intended destination.
   *
   * Resolves to null on success, or the one message to show for the failure.
   * The login page used to replace every failure with "Invalid email or
   * password", including the server's own 429 ("Too many attempts…") and 503
   * ("Sign-in is temporarily unavailable…") wording (ops audit 2026-10-02,
   * shell #12), so the inline error now shows exactly what the toast shows.
   */
  async function login(credentials: LoginRequest): Promise<string | null> {
    try {
      await authStore.login(credentials)

      showSuccess('Welcome back!', `Logged in as ${authStore.userDisplayName}`)

      // Redirect to intended destination or dashboard
      const redirect = route.query.redirect as string | undefined
      await router.push(redirect || '/')

      return null
    } catch (error) {
      const message = loginFailureMessage(error)
      showError(isCredentialRejection(error) ? 'Login failed' : "Couldn't sign you in", message)
      return message
    }
  }

  /**
   * Logout and redirect to login
   */
  async function logout(): Promise<void> {
    await authStore.logout()
    showSuccess('Logged out', 'You have been logged out')
    await router.push('/login')
  }

  /**
   * Check if user has required role
   */
  function hasRole(role: 'admin' | 'staff' | 'client' | 'developer'): boolean {
    return authStore.hasRole(role)
  }

  /**
   * Check if user has any of the required roles
   */
  function hasAnyRole(roles: Array<'admin' | 'staff' | 'client' | 'developer'>): boolean {
    return authStore.hasAnyRole(roles)
  }

  return {
    // State
    user,
    isAuthenticated,
    isLoading,
    isAdmin,
    isStaff,
    displayName,
    initials,

    // Actions
    login,
    logout,
    hasRole,
    hasAnyRole,
  }
}
